/**
 * What a member's SmartAssistant settings mean when a column holds no value
 * (TX-5174).
 *
 * The `user_settings` columns below are nullable. A NULL used to mean Off on
 * the settings page (`|| false`) and On in playback (`!== false`), so the page
 * and the assistant disagreed. Every reader now resolves NULL through this
 * module, to what members already experience today, so nothing changes
 * silently for them.
 *
 * `@sukha/db` keeps the Drizzle column defaults equal to
 * `SMART_ASSISTANT_SETTING_DEFAULTS` with a unit test.
 */

/**
 * The column defaults: what a member with no `user_settings` row gets
 * (`buildDefaultUserSettings`), and what a new row is created with.
 */
export const SMART_ASSISTANT_SETTING_DEFAULTS = {
  assistantAudioEnabled: true,
  smartAssistantAudioVolume: 50,
  generalTaskHelp: true,
  wordsOfEncouragement: true,
  phoneAndWebSiteNudge: false,
} as const;

/**
 * What a NULL column means: the column default, except assistant audio.
 *
 * - `assistantAudioEnabled` NULL is OFF. Playback has always skipped on a
 *   falsy value, so these members hear nothing today; turning it on would start
 *   speaking to them unannounced. (No row at all is still on: that member's
 *   app already plays audio from the no-row default.)
 * - `smartAssistantAudioVolume` NULL is 50: playback already played at 50.
 * - `generalTaskHelp` / `wordsOfEncouragement` NULL are on: playback already
 *   treated them as on (`!== false`).
 * - `phoneAndWebSiteNudge` NULL is off: the switch showed off, and no audio
 *   path reads the column.
 */
export const SMART_ASSISTANT_NULL_SETTINGS = {
  ...SMART_ASSISTANT_SETTING_DEFAULTS,
  assistantAudioEnabled: false,
} as const;

export type SmartAssistantSettingKey = keyof typeof SMART_ASSISTANT_SETTING_DEFAULTS;

export const SMART_ASSISTANT_SETTING_KEYS = Object.keys(
  SMART_ASSISTANT_SETTING_DEFAULTS
) as SmartAssistantSettingKey[];

/** The resolved value type of each setting (`boolean` / `number`, never null). */
export type SmartAssistantSettingValues = {
  [K in SmartAssistantSettingKey]: (typeof SMART_ASSISTANT_SETTING_DEFAULTS)[K] extends boolean
    ? boolean
    : number;
};

type StoredSmartAssistantSettings = {
  [K in SmartAssistantSettingKey]?: SmartAssistantSettingValues[K] | null;
};

/**
 * Returns `row` with each NULL or missing SmartAssistant column replaced by
 * its `SMART_ASSISTANT_NULL_SETTINGS` value. A value the member set (including `false` and `0`) is
 * kept; every other field passes through untouched. Does not mutate `row`.
 */
export function resolveSmartAssistantSettings<T extends StoredSmartAssistantSettings>(
  row: T
): Omit<T, SmartAssistantSettingKey> & SmartAssistantSettingValues {
  const resolved: Record<string, unknown> = { ...row };
  for (const key of SMART_ASSISTANT_SETTING_KEYS) {
    resolved[key] = row[key] ?? SMART_ASSISTANT_NULL_SETTINGS[key];
  }
  return resolved as Omit<T, SmartAssistantSettingKey> & SmartAssistantSettingValues;
}

export interface EffectiveAssistantSetting<T> {
  /** What the member actually gets. */
  value: T;
  /** True when the member's row holds a value; false when the default applies. */
  isSet: boolean;
}

export type EffectiveAssistantSettings = {
  [K in SmartAssistantSettingKey]: EffectiveAssistantSetting<SmartAssistantSettingValues[K]>;
};

/**
 * Per-field `{ value, isSet }` for a member's settings row, or for a member
 * with no row (`null`). For surfaces that must tell "chose this" from "never
 * touched it", such as the admin member page.
 */
export function effectiveAssistantSettings(
  row: StoredSmartAssistantSettings | null | undefined
): EffectiveAssistantSettings {
  // No row: the member is served the column defaults. A row: NULLs resolve.
  const resolved = row ? resolveSmartAssistantSettings(row) : SMART_ASSISTANT_SETTING_DEFAULTS;
  const result: Record<string, EffectiveAssistantSetting<unknown>> = {};
  for (const key of SMART_ASSISTANT_SETTING_KEYS) {
    const stored = row?.[key];
    result[key] = { value: resolved[key], isSet: stored !== null && stored !== undefined };
  }
  return result as EffectiveAssistantSettings;
}
